import { jwtDecode } from 'jwt-decode';
import { Environment } from 'types';
import { newVerifier, hashVerifier, encode } from './codes';
import { UserAuth } from './apiClient';
import { fallbackErrorMessage } from './constants';
// Namespaced because this module exports its own `authHost` helper.
import * as env from './env';

interface UserCredentials {
  email: string;
  password: string;
}

export interface LoginChallenge {
  challenge: string;
  location?: string;
  verifier: string;
}

type LoginResponse = UserAuth | LoginChallenge;

export function isLoginChallenge(x: any): x is LoginChallenge {
  return typeof x.verifier === 'string' && typeof x.challenge === 'string';
}

export async function login(
  { email, password }: UserCredentials,
  environment: Environment
): Promise<LoginResponse> {
  try {
    const verifier = await newVerifier();
    const challenge = await hashVerifier(verifier);
    const formData = {
      email,
      password,
      code_challenge: challenge,
      code_challenge_method: 'S256',
      encoded_data: getCognitoSecurityData(email, environment),
    };
    const response = await fetch(`${authHost(environment)}/auth`, {
      method: 'POST',
      body: JSON.stringify(formData),
      redirect: 'error',
    });
    if (response.ok) {
      const data = await response.json();

      if (data.challenge) {
        return {
          verifier: encode(verifier),
          location: data.location,
          challenge: data.challenge,
        };
      }

      const tokens = await exchange(data.location, verifier);
      return {
        ...tokens,
        initiate_mfa: data.initiate_mfa,
      };
    }
    throw message(response.status);
  } catch (err) {
    throw typeof err === 'string' ? err : message();
  }
}

async function exchange(
  location: string,
  verifier: Uint8Array
): Promise<UserAuth> {
  const url = location + '&code_verifier=' + encode(verifier);
  const response = await fetch(url);
  const data = await response.json();
  if (response.ok) {
    return data;
  }
  return Promise.reject(message(response.status));
}

function message(status?: number): string {
  return status === 401
    ? 'Invalid login details. Please try again.'
    : fallbackErrorMessage;
}

export async function signOut(
  tokens: UserAuth | undefined,
  environment: Environment
) {
  if (!tokens) return;
  if (expiresIn(tokens.access_token, 0)) {
    tokens = await refresh(tokens.refresh_token, tokens.id_token, environment);
  }
  if (tokens?.access_token) {
    fetch(`${authHost(environment)}/signout`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ access_token: tokens.access_token }),
    });
  }
}

export async function refresh(
  refreshToken: string,
  idToken: string,
  environment: Environment
) {
  const response = await fetch(`${authHost(environment)}/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      refresh_token: refreshToken,
      email: emailFromId(idToken),
    }),
  });

  if (response.ok) {
    return await response.json();
  } else {
    return Promise.reject('Please log in');
  }
}

export function expiresIn(token: string, minutes: number): boolean {
  try {
    const { exp } = jwtDecode(token);
    if (typeof exp !== 'number') return false;
    const nowSeconds = Math.ceil(Date.now() / 1000);
    return exp - minutes * 60 < nowSeconds;
  } catch {
    return false;
  }
}

export function emailFromId(token: string): string | undefined {
  try {
    return jwtDecode<{ email?: string }>(token).email;
  } catch {
    return undefined;
  }
}

/**
 * Cognito group names from an access token's `cognito:groups` claim.
 *
 * Returns `[]` for a malformed token, an absent claim, or a claim that is not
 * an array — a token we cannot read means no group, never an exception. The
 * caller gates a feature flag on this, so failing open to "no override" is the
 * safe direction.
 */
export function groupsFromAccess(token: string): string[] {
  try {
    const groups = jwtDecode<{ 'cognito:groups'?: unknown }>(token)[
      'cognito:groups'
    ];
    return Array.isArray(groups)
      ? groups.filter((group): group is string => typeof group === 'string')
      : [];
  } catch {
    return [];
  }
}

export function authHost(environment: Environment): string | undefined {
  return environment === 'pre-live' ? env.authHostPreLive : env.authHostLive;
}

const cognitoConfig = {
  clientId: {
    live: env.cognitoClientIdLive,
    'pre-live': env.cognitoClientIdPreLive,
  },
  userPoolId: {
    live: env.cognitoUserPoolIdLive,
    'pre-live': env.cognitoUserPoolIdPreLive,
  },
};

/**
 * This returns the encoded data string used for Cognito advanced security feature.
 * It depends on the amazon-cognito-advanced-security-data.min.js script which
 * must be loaded from Amazon's servers (referenced in `/public/index.html`).
 **/
function getCognitoSecurityData(
  username: string,
  env: Environment
): string | undefined {
  const getData = (window as any).AmazonCognitoAdvancedSecurityData?.getData;

  if (typeof getData === 'function') {
    return getData(
      username,
      cognitoConfig.userPoolId[env],
      cognitoConfig.clientId[env]
    );
  }
}
